<template>
  <div class="reports">
    <!-- sticky chrome: what this tab answers, the period, the export, the tabs -->
    <div class="reports__chrome">
      <div class="reports__head">
        <div class="reports__headtext">
          <div class="reports__eyebrow">Reportes</div>
          <h1 class="reports__title">{{ cfg.title }}</h1>
          <div class="reports__question">{{ cfg.question }}</div>
        </div>

        <div class="reports__controls">
          <div class="reports__seg">
            <button
              v-for="r in ranges"
              :key="r.days"
              type="button"
              :class="{ on: range === r.days }"
              @click="setRange(r.days)"
            >{{ r.label }}</button>
          </div>
          <button type="button" class="reports__csv" :disabled="!rows.length" @click="exportCsv">
            <hero-icon :icon="DownloadIcon" :size="15" />
            CSV
          </button>
        </div>
      </div>

      <div class="reports__tabs">
        <button
          v-for="(t, i) in tabs"
          :key="t.key"
          type="button"
          class="reports__tab"
          :class="{ 'reports__tab--on': tab === i }"
          @click="setTab(i)"
        >
          {{ t.label }}
          <span class="reports__slider"></span>
        </button>
      </div>
    </div>

    <div class="reports__body">
      <!-- venta / alquiler, where the metric can be split by operation -->
      <div v-if="opFilterable" class="reports__ops">
        <button
          v-for="o in opFilters"
          :key="o.label"
          type="button"
          class="reports__op"
          :class="{ 'reports__op--on': opFilter === o.value }"
          @click="setOpFilter(o.value)"
        >{{ o.label }}</button>
      </div>

      <!-- LEVEL 1 · the numbers -->
      <report-kpis :kpis="kpis" />

      <!-- LEVEL 2 · the one chart -->
      <section class="reports__hero">
        <div class="reports__herohead">
          <span class="reports__herolabel">{{ cfg.heroLabel }}</span>
          <span class="reports__herohint">línea punteada = período anterior</span>
        </div>
        <apexchart
          v-if="heroSeries[0] && heroSeries[0].data.length"
          :key="'hero-' + cfg.key + '-' + range"
          type="line"
          :height="wide ? 240 : 190"
          :options="heroOptions"
          :series="heroSeries"
        />
        <div v-else class="reports__heroempty">Sin datos en este período</div>
      </section>

      <!-- LEVEL 3 · independent detail panels -->
      <div v-if="panels.length" class="reports__panels">
        <report-panel
          v-for="(p, i) in panels"
          :key="cfg.key + ':' + i"
          :panel="p"
          :open="isPanelOpen(i)"
          @toggle="togglePanel(i)"
        >
          <!-- the two breakdowns that already had their own drill-downs keep them,
               now folded into the panel grid instead of sitting loose on the page -->
          <areas-panel v-if="p.slot === 'areas'" :range="range" :op-filter="opFilter" flush />
          <demand-panel v-else-if="p.slot === 'budget'" :range="range" flush />
        </report-panel>
      </div>

      <!-- LEVEL 4 · the work -->
      <div class="reports__tablewrap">
        <report-table
          :tab-key="cfg.key"
          :title="cfg.tableTitle"
          :columns="cfg.columns"
          :rows="tableRows"
          :actions="rowActions"
          :total-count="totalCount"
          :sort="sort"
          :dir="dir"
          :search="search"
          :loading="tableLoading"
          :has-more="hasMore"
          :wide="wide"
          :default-hidden="cfg.defaultHidden || {}"
          @sort="onSort"
          @search="onSearch"
          @load-more="loadMore"
          @open="openRow"
          @action="onAction"
          @notice="flash"
        />
      </div>
    </div>

    <report-drawer
      v-model="drawerOpen"
      :detail="drawerDetail"
      :timeline="drawerTimeline"
      :timeline-loading="timelineLoading"
      :wide="wide"
      @action="onDrawerAction"
    />

    <property-detail ref="detail" />
    <inquiry-detail-dialog ref="inquiryDetail" @reload="fetchTable" />
    <visitor-journey-dialog ref="journey" @open-property="onJourneyProperty" @open-inquiry="openInquiryById" />

    <v-snackbar v-model="toast" :timeout="2400" bottom color="grey darken-4">{{ toastText }}</v-snackbar>
  </div>
</template>

<script>
// Reportes, in four levels: the numbers, the one chart, the breakdowns you can
// fold away, and the list you actually work. Each tab is one question — the tab
// config (utils/reportsConfig) says which KPIs answer it, which columns its table
// has and how a row maps; this view fetches and wires, it doesn't hard-code tabs.
//
// Every figure is shown against the previous window of the same length: the same
// queries are run twice with shifted dates, which is where the KPI deltas and the
// hero's dotted comparison line come from.
import {
  VisitReport,
  AudienceReport,
  AudienceVisitors,
  RegisteredUsers,
  AreaReport,
  DemandByBudget,
  PropertyPerformance,
  OwnerPortfolio,
  VisitorJourney
} from "@/graphql/queries/analytics";
import { Inquiries } from "@/graphql/queries/inquiries";
import PropertyDetail from "@/components/properties/Detail";
import VisitorJourneyDialog from "@/components/reports/VisitorJourneyDialog";
import AreasPanel from "@/components/reports/AreasPanel";
import DemandPanel from "@/components/reports/DemandPanel";
import InquiryDetailDialog from "@/components/inquiries/InquiryDetailDialog";
import ReportKpis from "@/components/reports/ReportKpis";
import ReportPanel from "@/components/reports/ReportPanel";
import ReportTable from "@/components/reports/ReportTable";
import ReportDrawer from "@/components/reports/ReportDrawer";
import HeroIcon from "@/components/shared/HeroIcon";
import DownloadIcon from "heroicons/24/outline/arrow-down-tray.svg";
import OpenIcon from "heroicons/24/outline/chevron-right.svg";
import ExtIcon from "heroicons/24/outline/arrow-top-right-on-square.svg";
import PhoneIcon from "heroicons/24/outline/phone.svg";
import ChatIcon from "heroicons/24/outline/chat-bubble-left-right.svg";
import ClockIcon from "heroicons/24/outline/clock.svg";
import ReportIcon from "heroicons/24/outline/document-arrow-down.svg";
import { comparisonOptions, fillDaily, localDateKey, BROWSER_TZ, ACCENT } from "@/utils/charts";
import { TABS, mapRow, buildPanels, daysSince, pct, categoryLabel, GOLD, GREEN, BLUE } from "@/utils/reportsConfig";
import staffOnly from "@/mixins/staffOnly";

const PANELS_KEY = "sartor.reportes.panels";
const PER = 25;

export default {
  mixins: [staffOnly],

  components: {
    ReportKpis,
    ReportPanel,
    ReportTable,
    ReportDrawer,
    PropertyDetail,
    VisitorJourneyDialog,
    AreasPanel,
    DemandPanel,
    InquiryDetailDialog,
    HeroIcon
  },

  data: () => ({
    DownloadIcon,
    tabs: TABS,
    tab: 0,
    range: 30,
    ranges: [
      { days: 7, label: "7 d" },
      { days: 30, label: "30 d" },
      { days: 90, label: "90 d" },
      { days: 180, label: "6 m" }
    ],
    opFilter: null,
    opFilters: [
      { value: null, label: "Todas" },
      { value: "sell", label: "Venta" },
      { value: "rent", label: "Alquiler" }
    ],

    // shared reports (current + the window before it)
    visit: {},
    visitPrev: {},
    audience: {},
    audiencePrev: {},
    area: {},
    budget: {},

    // active tab's table
    rawRows: [],
    totalCount: 0,
    pages: 1,
    page: 1,
    sort: "",
    dir: "desc",
    search: "",
    tableLoading: false,
    searchTimer: null,

    panelsOpen: {},
    selected: null,
    drawerTimeline: [],
    timelineLoading: false,
    toast: false,
    toastText: "",
    vw: 1440
  }),

  computed: {
    cfg() {
      return TABS[this.tab];
    },

    wide() {
      return this.vw >= 1200;
    },

    // the operation split only means anything where the metric is a property's
    opFilterable() {
      return ["demanda", "consultas"].includes(this.cfg.key);
    },

    rangeDates() {
      return this.datesFor(0);
    },

    prevDates() {
      return this.datesFor(this.range);
    },

    // ---------- level 1 ----------
    kpis() {
      const k = this.cfg.key;
      const v = this.visit || {};
      const vp = this.visitPrev || {};
      const a = this.audience || {};
      const ap = this.audiencePrev || {};

      if (k === "demanda") {
        // visitReport's totalVisits/totalInquiries are LIFETIME counters; under a
        // period selector the number to show — and the only one whose delta means
        // anything — is the range, which is what the daily buckets add up to.
        const visits = this.sum(v.daily);
        const visitsPrev = this.sum(vp.daily);
        const inq = this.sum(v.dailyInquiries);
        const inqPrev = this.sum(vp.dailyInquiries);
        return [
          this.kpi(visits, visitsPrev, "Visitas"),
          this.kpi(v.totalSearches, vp.totalSearches, "Búsquedas"),
          this.kpi(inq, inqPrev, "Consultas"),
          this.kpiPct(this.ratio(inq, visits), this.ratio(inqPrev, visitsPrev), "Visita → consulta")
        ];
      }
      if (k === "consultas") {
        return [
          this.kpi(this.sum(v.dailyInquiries), this.sum(vp.dailyInquiries), "Consultas del período"),
          this.kpi(v.openInquiries, vp.openInquiries, "Abiertas", { invert: true }),
          this.kpi(v.unansweredOver5d, vp.unansweredOver5d, "Sin respuesta >5 d", {
            invert: true,
            accent: true
          }),
          this.kpiDays(v.medianFirstResponseDays, vp.medianFirstResponseDays, "Mediana 1ª respuesta")
        ];
      }
      if (k === "leads") {
        const hot = (this.rawRows || []).filter(r => r.leadTier === "hot").length;
        return [
          { value: this.fmt(hot), label: "Calientes", accent: true },
          this.kpi(a.identifiedVisitors, ap.identifiedVisitors, "Identificados"),
          this.kpi(a.uniqueVisitors, ap.uniqueVisitors, "Visitantes con intención"),
          this.kpiPct(a.conversion, ap.conversion, "Consulta → ganada")
        ];
      }
      if (k === "propietarios") {
        return [
          { value: this.fmt(this.totalCount), label: "Propiedades con dueño" },
          this.kpiPct(a.newListingsLiquidity, ap.newListingsLiquidity, "Nuevos con consulta ≤14 d"),
          this.kpiDays(a.timeToFirstInquiryDays, ap.timeToFirstInquiryDays, "Mediana a 1ª consulta"),
          this.kpiPct(a.buyerLiquidity, ap.buyerLiquidity, "Liquidez de listados")
        ];
      }
      return [
        this.kpi(a.newUsers, ap.newUsers, "Cuentas nuevas"),
        { value: this.fmt(a.totalUsers), label: "Total de cuentas" },
        this.kpiPct(this.onboardingRate(a), this.onboardingRate(ap), "Invitación → cuenta"),
        this.kpi(this.onboardingShown(a), this.onboardingShown(ap), "Invitaciones mostradas")
      ];
    },

    // ---------- level 2 ----------
    heroBuckets() {
      const k = this.cfg.key;
      const v = this.visit || {};
      const vp = this.visitPrev || {};
      const a = this.audience || {};
      const ap = this.audiencePrev || {};
      if (k === "consultas") return [v.dailyInquiries, vp.dailyInquiries];
      if (k === "leads") return [a.dailyVisitors, ap.dailyVisitors];
      if (k === "cuentas") return [a.dailyNewUsers, ap.dailyNewUsers];
      return [v.daily, vp.daily];
    },

    heroSeries() {
      const [now, prev] = this.heroBuckets;
      const current = fillDaily(now, this.range).map(p => p.y);
      const before = this.fillPrev(prev);
      return [
        { name: "Ahora", type: "area", data: current },
        { name: "Anterior", type: "line", data: before }
      ];
    },

    heroOptions() {
      return comparisonOptions(this.heroLabels, ACCENT);
    },

    heroLabels() {
      return fillDaily(null, this.range).map(p => {
        const [, m, d] = p.x.split("-");
        return d + "/" + m;
      });
    },

    // ---------- level 3 ----------
    panels() {
      return buildPanels(this.cfg.key, {
        visit: this.visit,
        audience: this.audience,
        area: this.area,
        budget: this.budget,
        leads: this.cfg.key === "leads" ? this.rawRows : null
      });
    },

    // ---------- level 4 ----------
    // leads + cuentas arrive whole (a few hundred rows), so their sorting and
    // search happen here; the other three are sorted and paged by the server.
    clientSorted() {
      return !this.cfg.serverSorted;
    },

    processedRows() {
      if (!this.clientSorted) return this.rawRows;

      const q = this.search.trim().toLowerCase();
      let list = this.rawRows;
      if (q) {
        list = list.filter(r =>
          Object.values(r).some(v => v != null && typeof v !== "object" && String(v).toLowerCase().includes(q))
        );
      }
      const key = this.sort;
      const sign = this.dir === "asc" ? 1 : -1;
      return list.slice().sort((a, b) => {
        const x = this.sortValue(a, key);
        const y = this.sortValue(b, key);
        if (typeof x === "number" && typeof y === "number") return (x - y) * sign;
        return String(x).localeCompare(String(y), "es") * sign;
      });
    },

    visibleRows() {
      return this.clientSorted ? this.processedRows.slice(0, this.page * PER) : this.rawRows;
    },

    tableRows() {
      return this.visibleRows.map(r => {
        const mapped = mapRow(this.cfg.key, r, this.$t.bind(this));
        return { ...mapped, raw: r };
      });
    },

    hasMore() {
      return this.clientSorted ? this.visibleRows.length < this.processedRows.length : this.page < this.pages;
    },

    rows() {
      return this.tableRows;
    },

    rowActions() {
      const k = this.cfg.key;
      const open = { key: "open", label: "Abrir", icon: OpenIcon };
      if (k === "demanda") return [open, { key: "web", label: "Ver en la web", icon: ExtIcon }];
      if (k === "consultas") return [open, { key: "reply", label: "Responder", icon: ChatIcon }];
      if (k === "leads") return [open, { key: "journey", label: "Recorrido", icon: ClockIcon }];
      if (k === "propietarios") return [open, { key: "ownerCsv", label: "Informe", icon: ReportIcon }];
      return [open, { key: "journey", label: "Recorrido", icon: ClockIcon }];
    },

    drawerOpen: {
      get() {
        return !!this.selected;
      },
      set(v) {
        if (!v) this.selected = null;
      }
    },

    drawerDetail() {
      const r = this.selected;
      if (!r) return { badges: [], facts: [], actions: [] };
      return this.detailFor(this.cfg.key, r);
    }
  },

  watch: {
    tab() {
      this.resetTable();
      this.fetchTable();
      this.fetchTabExtras();
    }
  },

  created() {
    this.sort = this.cfg.defaultSort;
    try {
      this.panelsOpen = JSON.parse(window.localStorage.getItem(PANELS_KEY) || "null") || {
        "demanda:0": true,
        "leads:0": true
      };
    } catch (e) {
      this.panelsOpen = {};
    }
    this.fetchReports();
    this.fetchTable();
    this.fetchTabExtras();
  },

  mounted() {
    this.vw = window.innerWidth;
    this.onResize = () => {
      this.vw = window.innerWidth;
    };
    window.addEventListener("resize", this.onResize);
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
    clearTimeout(this.searchTimer);
  },

  methods: {
    // ---------- dates ----------
    // `back` shifts the whole window that many days into the past, which is how
    // the previous-period comparison is built
    datesFor(back) {
      const to = new Date();
      to.setDate(to.getDate() - back);
      const from = new Date(to);
      from.setDate(to.getDate() - (this.range - 1));
      return { from: localDateKey(from), to: localDateKey(to) };
    },

    // ---------- KPI helpers ----------
    fmt(n) {
      return n == null ? "—" : Number(n).toLocaleString("es-AR");
    },

    // daily buckets → the range total (the lifetime counters can't be compared)
    sum(buckets) {
      if (!buckets) return null;
      return buckets.reduce((n, b) => n + (b.count || 0), 0);
    },

    ratio(part, whole) {
      if (!whole) return null;
      return (Number(part || 0) / Number(whole)) * 100;
    },

    // `invert` = a rise is bad news (open/unanswered consultas)
    kpi(now, prev, label, o = {}) {
      const value = this.fmt(now);
      if (now == null || prev == null || !prev) return { value, label, accent: o.accent };
      const diff = now - prev;
      const up = diff > 0;
      return {
        value,
        label,
        accent: o.accent,
        delta: diff === 0 ? "" : (up ? "+" : "−") + this.fmt(Math.abs(diff)),
        up: o.invert ? !up : up
      };
    },

    kpiPct(now, prev, label) {
      const n = Number(now);
      if (now == null || !Number.isFinite(n)) return { value: "—", label };
      const value = pct(n);
      const p = Number(prev);
      if (prev == null || !Number.isFinite(p)) return { value, label };
      const diff = Math.round((n - p) * 10) / 10;
      return {
        value,
        label,
        delta: diff === 0 ? "" : (diff > 0 ? "+" : "−") + String(Math.abs(diff)).replace(".", ",") + " pp",
        up: diff > 0
      };
    },

    // fewer days is better, so the arrow flips
    kpiDays(now, prev, label) {
      const n = Number(now);
      if (now == null || !Number.isFinite(n)) return { value: "—", label };
      const value = String(n).replace(".", ",") + " d";
      const p = Number(prev);
      if (prev == null || !Number.isFinite(p)) return { value, label };
      const diff = Math.round((n - p) * 10) / 10;
      return {
        value,
        label,
        delta: diff === 0 ? "" : (diff > 0 ? "+" : "−") + String(Math.abs(diff)).replace(".", ",") + " d",
        up: diff < 0
      };
    },

    onboardingShown(a) {
      const f = (a && a.onboardingFunnel) || [];
      return f.length ? f[0].count : null;
    },

    onboardingRate(a) {
      const f = (a && a.onboardingFunnel) || [];
      if (f.length < 2 || !f[0].count) return null;
      return (f[f.length - 1].count / f[0].count) * 100;
    },

    // the previous window's buckets, aligned to the current window's length
    fillPrev(buckets) {
      const map = {};
      (buckets || []).forEach(b => {
        map[b.label] = b.count;
      });
      const out = [];
      const end = new Date();
      end.setDate(end.getDate() - this.range);
      for (let i = this.range - 1; i >= 0; i--) {
        const d = new Date(end);
        d.setDate(end.getDate() - i);
        out.push(map[localDateKey(d)] || 0);
      }
      return out;
    },

    // ---------- fetching ----------
    reportVars(dates) {
      return {
        from: dates.from,
        to: dates.to,
        timeZone: BROWSER_TZ,
        operationType: this.opFilter
      };
    },

    query(query, variables) {
      return this.$apollo.query({ query, variables, fetchPolicy: "no-cache" }).then(r => r.data);
    },

    fetchReports() {
      const cur = this.reportVars(this.rangeDates);
      const prev = this.reportVars(this.prevDates);
      this.query(VisitReport, cur).then(d => {
        this.visit = d.visitReport || {};
      });
      this.query(VisitReport, prev).then(d => {
        this.visitPrev = d.visitReport || {};
      });
      this.query(AudienceReport, cur).then(d => {
        this.audience = d.audienceReport || {};
      });
      this.query(AudienceReport, prev).then(d => {
        this.audiencePrev = d.audienceReport || {};
      });
    },

    // panel data only the Demanda tab needs
    fetchTabExtras() {
      if (this.cfg.key !== "demanda") return;
      const v = this.reportVars(this.rangeDates);
      this.query(AreaReport, v).then(d => {
        this.area = d.areaReport || {};
      });
      this.query(DemandByBudget, {
        from: v.from,
        to: v.to,
        timeZone: BROWSER_TZ,
        operationType: this.opFilter || "sell"
      }).then(d => {
        this.budget = d.demandByBudget || {};
      });
    },

    fetchTable(append = false) {
      const k = this.cfg.key;
      this.tableLoading = true;
      const done = rows => {
        this.rawRows = append ? this.rawRows.concat(rows) : rows;
        this.tableLoading = false;
      };
      const fail = () => {
        this.tableLoading = false;
      };
      const dates = this.rangeDates;

      if (k === "demanda" || k === "propietarios") {
        const isDemand = k === "demanda";
        const vars = {
          from: dates.from,
          to: dates.to,
          timeZone: BROWSER_TZ,
          search: this.search || null,
          sort: this.sort,
          dir: this.dir,
          page: this.page,
          per: PER
        };
        if (isDemand) vars.operationType = this.opFilter;
        return this.query(isDemand ? PropertyPerformance : OwnerPortfolio, vars)
          .then(d => {
            const res = (isDemand ? d.propertyPerformance : d.ownerPortfolio) || {};
            this.totalCount = res.totalCount || 0;
            this.pages = res.pages || 1;
            done(res.rows || []);
          })
          .catch(fail);
      }

      if (k === "consultas") {
        return this.query(Inquiries, {
          type: "all",
          page: this.page,
          perPage: PER,
          search: this.search || null,
          operationType: this.opFilter,
          order: this.sort
        })
          .then(d => {
            const res = d.inquiries || {};
            this.pages = res.pages || 1;
            // the inquiries query reports pages, not a total — derive one so the
            // header still reads "N de M" (exact on the last page)
            this.totalCount = Math.max(
              (this.pages - 1) * PER + ((res.inquiries || []).length || 0),
              this.rawRows.length
            );
            done(res.inquiries || []);
          })
          .catch(fail);
      }

      if (k === "leads") {
        return this.query(AudienceVisitors, {
          from: dates.from,
          to: dates.to,
          timeZone: BROWSER_TZ,
          limit: 600
        })
          .then(d => {
            const rows = d.audienceVisitors || [];
            this.totalCount = rows.length;
            done(rows);
          })
          .catch(fail);
      }

      return this.query(RegisteredUsers, { limit: 600 })
        .then(d => {
          const rows = d.registeredUsers || [];
          this.totalCount = rows.length;
          done(rows);
        })
        .catch(fail);
    },

    resetTable() {
      this.rawRows = [];
      this.page = 1;
      this.pages = 1;
      this.totalCount = 0;
      this.search = "";
      this.sort = this.cfg.defaultSort;
      this.dir = "desc";
      if (!this.opFilterable) this.opFilter = null;
    },

    // ---------- controls ----------
    setTab(i) {
      if (this.tab === i) return;
      this.tab = i;
    },

    setRange(days) {
      if (this.range === days) return;
      this.range = days;
      this.page = 1;
      this.rawRows = [];
      this.fetchReports();
      this.fetchTable();
      this.fetchTabExtras();
    },

    setOpFilter(value) {
      if (this.opFilter === value) return;
      this.opFilter = value;
      this.page = 1;
      this.rawRows = [];
      this.fetchReports();
      this.fetchTable();
      this.fetchTabExtras();
    },

    onSort(key) {
      const sameKey = this.sort === key;
      this.dir = sameKey && this.dir === "desc" ? "asc" : "desc";
      this.sort = key;
      this.page = 1;
      if (!this.clientSorted) {
        this.rawRows = [];
        this.fetchTable();
      }
    },

    onSearch(text) {
      this.search = text;
      this.page = 1;
      if (this.clientSorted) return;
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.rawRows = [];
        this.fetchTable();
      }, 320);
    },

    loadMore() {
      if (this.tableLoading || !this.hasMore) return;
      this.page += 1;
      if (!this.clientSorted) this.fetchTable(true);
    },

    sortValue(row, key) {
      const mapped = mapRow(this.cfg.key, row, this.$t.bind(this));
      const keys = mapped.sortKeys || {};
      return keys[key] != null ? keys[key] : "";
    },

    // ---------- panels ----------
    panelKey(i) {
      return this.cfg.key + ":" + i;
    },

    isPanelOpen(i) {
      return !!this.panelsOpen[this.panelKey(i)];
    },

    togglePanel(i) {
      const key = this.panelKey(i);
      this.$set(this.panelsOpen, key, !this.panelsOpen[key]);
      try {
        window.localStorage.setItem(PANELS_KEY, JSON.stringify(this.panelsOpen));
      } catch (e) {
        /* private mode — the preference just won't survive the session */
      }
    },

    // ---------- drawer ----------
    openRow(row) {
      this.selected = row;
      this.drawerTimeline = [];
      this.loadTimeline(row);
    },

    // the recorrido comes from the same visitorJourney the Consultas drawer uses,
    // for whichever id this row carries
    loadTimeline(row) {
      const leadId = row.leadId || null;
      const visitorId = row.visitorId || (this.cfg.key === "leads" ? row.id : null);
      if (!leadId && !visitorId) return;

      this.timelineLoading = true;
      this.query(VisitorJourney, { leadId, visitorId: leadId ? null : visitorId, limit: 12 })
        .then(d => {
          const events = (d.visitorJourney && d.visitorJourney.events) || [];
          this.drawerTimeline = events.slice(0, 12).map(e => ({
            kind: this.eventKind(e.kind),
            when: this.fmtWhen(e.at),
            title: e.title,
            detail: e.detail,
            dot: e.kind === "search" ? ACCENT : e.kind === "inquiry" ? GREEN : GOLD
          }));
          this.timelineLoading = false;
        })
        .catch(() => {
          this.timelineLoading = false;
        });
    },

    eventKind(kind) {
      return { search: "búsqueda", visit: "visita", inquiry: "consulta", chat: "chat" }[kind] || kind;
    },

    fmtWhen(at) {
      if (!at) return "";
      const d = daysSince(at);
      return d === 0 ? "hoy" : d === 1 ? "ayer" : "hace " + d + " d";
    },

    detailFor(key, r) {
      const b = (label, bg, fg) => ({ label, bg, fg });
      const f = (label, value) => ({ label, value: value == null || value === "" ? "—" : String(value) });
      const days = d => (d == null ? "—" : d === 0 ? "hoy" : "hace " + d + " d");

      if (key === "demanda") {
        return {
          kicker: "Propiedad",
          badges: r.zone ? [b(r.zone, "#e1ecff", BLUE)] : [],
          title: r.property ? r.property.fullAddress : "Propiedad",
          sub: r.price,
          actions: [
            { key: "detail", label: "Abrir ficha", icon: OpenIcon, tone: "ink" },
            { key: "web", label: "Ver en la web", icon: ExtIcon }
          ],
          facts: [
            f("Zona", r.zone),
            f("Precio", r.price),
            f("Visitas del período", this.fmt(r.rangeVisits)),
            f("Visitas totales", this.fmt(r.visits)),
            f("Consultas", this.fmt(r.inquiries)),
            f("Conversión", pct(r.conversion)),
            f("Últ. visita", days(r.daysSinceLastVisit))
          ]
        };
      }

      if (key === "consultas") {
        return {
          kicker: "Consulta",
          badges: [b(r.attended ? "Atendida" : "Sin responder", r.attended ? "#e3f3e8" : "#fdecea", r.attended ? GREEN : ACCENT)],
          title: r.name,
          sub: r.property ? r.property.fullAddress : "Consulta general",
          actions: [
            { key: "reply", label: "Abrir consulta", icon: ChatIcon, tone: "accent" },
            { key: "journey", label: "Ver recorrido", icon: ClockIcon }
          ],
          facts: [
            f("Entró", days(daysSince(r.createdAtTime || r.createdAt))),
            f("Esperando", r.attended ? "atendida" : r.waitingDays != null ? r.waitingDays + " d" : "—"),
            f("Teléfono", r.phone),
            f("Email", r.email),
            f("Puntaje", r.leadScore),
            f("Mensaje", r.message)
          ]
        };
      }

      if (key === "leads") {
        return {
          kicker: "Lead · recorrido completo",
          badges: [
            b(r.identified ? "Identificado" : "Anónimo", r.identified ? "#e3f3e8" : "#ece9e3", r.identified ? GREEN : "#6a645a"),
            r.device ? b(r.device, "#e9eef2", "#43596b") : null
          ].filter(Boolean),
          title: r.contactName || "Visitante anónimo",
          sub: r.identified ? [r.contactEmail, r.contactPhone].filter(Boolean).join(" · ") : "Sin datos de contacto todavía",
          actions: [
            { key: "journey", label: "Ver recorrido", icon: ClockIcon, tone: "ink" },
            { key: "call", label: "Llamar", icon: PhoneIcon }
          ],
          facts: [
            f("Intención", r.topOperation === "sell" ? "Venta" : r.topOperation === "rent" ? "Alquiler" : null),
            f("Categoría", r.topCategory ? categoryLabel(r.topCategory) : null),
            f("Zona", r.topZone),
            f("Puntaje", r.leadScore),
            f("Búsquedas", this.fmt(r.searchesCount)),
            f("Visitas", this.fmt(r.visitsCount)),
            f("Consultas", this.fmt(r.inquiriesCount)),
            f("Últ. actividad", days(daysSince(r.lastSeen)))
          ]
        };
      }

      if (key === "propietarios") {
        return {
          kicker: "Propiedad del propietario",
          badges: [
            b(r.published ? "Publicada" : "No publicada", r.published ? "#e3f3e8" : "#ece9e3", r.published ? GREEN : "#6a645a")
          ],
          title: r.property ? r.property.fullAddress : "Propiedad",
          sub: "Propietario: " + (r.ownerName || "—"),
          actions: [
            { key: "detail", label: "Abrir ficha", icon: OpenIcon, tone: "ink" },
            { key: "ownerCsv", label: "Informe del dueño", icon: ReportIcon }
          ],
          facts: [
            f("Propietario", r.ownerName),
            f("Estado", r.published ? "Publicada" : "No publicada"),
            f("Visitas totales", this.fmt(r.visits)),
            f("Visitas del período", this.fmt(r.rangeVisits)),
            f("Consultas", this.fmt(r.inquiries)),
            f("Abiertas", this.fmt(r.openInquiries))
          ]
        };
      }

      return {
        kicker: "Cuenta registrada",
        badges: [
          b(
            r.signupMethod === "google" ? "Google" : "Email",
            r.signupMethod === "google" ? "#e1ecff" : "#fdecea",
            r.signupMethod === "google" ? BLUE : ACCENT
          )
        ],
        title: r.name || "Sin nombre",
        sub: [r.email, r.phone].filter(Boolean).join(" · "),
        actions: [{ key: "journey", label: "Ver recorrido", icon: ClockIcon, tone: "ink" }],
        facts: [
          f("Email", r.email),
          f("Teléfono", r.phone),
          f("Registro", days(daysSince(r.registeredAt))),
          f("Últ. actividad", days(daysSince(r.lastActiveAt))),
          f("Favoritos", this.fmt(r.favoritesCount)),
          f("Búsquedas guardadas", this.fmt(r.savedSearchesCount)),
          f("Consultas", this.fmt(r.inquiriesCount))
        ]
      };
    },

    // ---------- actions ----------
    onAction({ action, row }) {
      if (action === "open") return this.openRow(row);
      return this.runAction(action, row);
    },

    onDrawerAction(action) {
      this.runAction(action, this.selected);
    },

    runAction(action, row) {
      if (!row) return;
      if (action === "detail") {
        this.selected = null;
        if (row.property) this.$refs.detail.open(row.property);
        return;
      }
      if (action === "web") {
        const p = row.property;
        if (!p) return this.flash("Esta fila no tiene una propiedad asociada");
        window.open("https://sartorpropiedades.com.ar/propiedad/" + p.id, "_blank", "noopener");
        return;
      }
      if (action === "reply") {
        this.selected = null;
        this.$refs.inquiryDetail.open(row.id);
        return;
      }
      if (action === "journey") {
        const leadId = row.leadId || null;
        const visitorId = row.visitorId || (this.cfg.key === "leads" ? row.id : null);
        if (!leadId && !visitorId) return this.flash("Este registro no tiene recorrido todavía");
        this.selected = null;
        this.$refs.journey.open(visitorId, leadId);
        return;
      }
      if (action === "call") {
        if (!row.contactPhone) return this.flash("Este visitante no está identificado");
        window.location.href = "tel:" + row.contactPhone;
        return;
      }
      if (action === "ownerCsv") this.exportOwnerCsv(row);
    },

    openInquiryById(id) {
      this.$refs.inquiryDetail.open(id);
    },

    onJourneyProperty(property) {
      this.$refs.detail.open(property);
    },

    flash(text) {
      this.toastText = text;
      this.toast = true;
    },

    // ---------- export ----------
    csvDownload(rows, name) {
      const csv = rows
        .map(row => row.map(c => `"${String(c == null ? "" : c).replace(/"/g, '""')}"`).join(","))
        .join("\n");
      const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    },

    // exports exactly what's on screen: the visible columns, in their order, for
    // the rows currently loaded
    exportCsv() {
      const table = this.$children.find(c => c.visibleColumns);
      const cols = table ? table.visibleColumns : this.cfg.columns;
      const head = ["Reportes · " + this.cfg.title];
      const period = ["Período", `Últimos ${this.range} días`, `${this.rangeDates.from} a ${this.rangeDates.to}`];
      const header = cols.map(c => c.label);
      const body = this.tableRows.map(r =>
        cols.map(c => {
          const cell = r.cells[c.key] || {};
          return [cell.badge, cell.text, cell.sub].filter(Boolean).join(" · ");
        })
      );
      this.csvDownload([head, period, [], header, ...body], `reportes-${this.cfg.key}-${this.range}d.csv`);
    },

    exportOwnerCsv(row) {
      const owned = this.rawRows.filter(r => r.ownerId === row.ownerId);
      const rows = [
        ["Reporte de propietario"],
        ["Propietario", row.ownerName || ""],
        ["Período", `Últimos ${this.range} días`, `${this.rangeDates.from} a ${this.rangeDates.to}`],
        [],
        ["Propiedad", "Estado", "Visitas totales", `Visitas (${this.range}d)`, "Consultas", "Abiertas"]
      ];
      owned.forEach(r =>
        rows.push([
          r.property ? r.property.fullAddress : "",
          r.published ? "Publicada" : "No publicada",
          r.visits,
          r.rangeVisits,
          r.inquiries,
          r.openInquiries
        ])
      );
      this.csvDownload(rows, `reporte-${(row.ownerName || "propietario").toLowerCase().replace(/\s+/g, "-")}.csv`);
      this.flash("Informe del propietario descargado");
    }
  }
};
</script>

<style scoped>
/* the page bleeds past .app-content's 1200px cap: a report is a wide surface */
.reports {
  margin: -28px -32px -56px;
  padding: 0;
  background: #f7f5f1;
  min-height: 100vh;
}
@media (max-width: 600px) {
  .reports { margin: -18px -16px -40px; }
}

/* ---- sticky chrome ---- */
.reports__chrome {
  position: sticky;
  top: 0;
  z-index: 5;
  background: #f7f5f1;
  border-bottom: 1px solid #ece7de;
  padding: 22px 32px 0;
}
@media (max-width: 1199px) {
  .reports__chrome { top: 56px; padding: 14px 14px 0; }
}

.reports__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}
.reports__headtext { min-width: 0; }
.reports__eyebrow {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #b52118;
  margin-bottom: 6px;
}
.reports__title { font-size: 24px; font-weight: 700; line-height: 1.1; color: #1a1814; margin: 0; }
.reports__question { font-size: 12.5px; color: #a39a8c; margin-top: 5px; }

.reports__controls { display: flex; align-items: center; gap: 8px; flex: none; }
.reports__seg { display: inline-flex; background: #f1ece3; border-radius: 9px; padding: 3px; gap: 2px; }
.reports__seg button {
  border: none;
  cursor: pointer;
  font-size: 12.5px;
  font-weight: 600;
  color: #6a645a;
  background: transparent;
  border-radius: 7px;
  padding: 7px 14px;
}
.reports__seg button.on {
  background: #fff;
  color: #1a1814;
  font-weight: 700;
  box-shadow: 0 1px 4px -1px rgba(40, 33, 22, 0.3);
}
.reports__csv {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  background: #fff;
  color: #1a1814;
  border: 1px solid #e3ddd2;
  border-radius: 8px;
  min-height: 36px;
  padding: 0 15px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.reports__csv:hover:not(:disabled) { background: #faf8f4; border-color: #dcd4c6; }
.reports__csv:disabled { opacity: 0.5; cursor: default; }

.reports__tabs { display: flex; gap: 2px; overflow-x: auto; }
.reports__tab {
  position: relative;
  flex: none;
  border: none;
  background: transparent;
  cursor: pointer;
  height: 44px;
  padding: 0 14px;
  font-size: 13.5px;
  font-weight: 600;
  color: #8a8377;
}
.reports__tab--on { color: #1a1814; }
.reports__slider {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 2px;
  border-radius: 2px 2px 0 0;
  background: transparent;
}
.reports__tab--on .reports__slider { background: #b52118; }

/* ---- body ---- */
.reports__body { padding: 22px 32px 56px; }
@media (max-width: 1199px) {
  .reports__body { padding: 14px 14px 40px; }
}

.reports__ops { display: flex; gap: 7px; margin-bottom: 14px; }
.reports__op {
  border: 1px solid #e3ddd2;
  background: #fff;
  color: #56504a;
  border-radius: 999px;
  padding: 7px 15px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
}
.reports__op--on { background: #1a1814; border-color: #1a1814; color: #fff; }

.reports__hero {
  background: #fff;
  border: 1px solid #ece7de;
  border-radius: 14px;
  padding: 18px 20px;
  box-shadow: 0 8px 24px -16px rgba(40, 33, 22, 0.18);
  margin-bottom: 14px;
}
.reports__herohead {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 6px;
}
.reports__herolabel {
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #a39a8c;
  font-weight: 600;
}
.reports__herohint { font-size: 11.5px; color: #a39a8c; }
.reports__heroempty { padding: 60px 0; text-align: center; color: #a39a8c; font-size: 13.5px; }

.reports__panels {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 14px;
}
@media (max-width: 1199px) {
  .reports__panels { grid-template-columns: 1fr; }
}

/* the table keeps its column minimums and scrolls sideways rather than squeezing */
.reports__tablewrap { overflow-x: auto; }
</style>
